import { BoundingBox, Matrix, Plane, Ray, Vector3 } from '@babylonjs/core'

type Range = { min: number; max: number }

/**
 * Map value in given range to another range
 * @param value - value that should be mapped
 * @param fromRange - initial range
 * @param toRange - targeted range
 * @returns mapped value
 */
export const mapValue = (value: number, fromRange: Range, toRange: Range) => {
  const fromSize = fromRange.max - fromRange.min
  if (fromSize === 0) return toRange.min

  const toSize = toRange.max - toRange.min
  const fromPercent = (value - fromRange.min) / fromSize
  return fromPercent * toSize + toRange.min
}

/**
 * Compares two numbers with given precision
 * @param a first operand
 * @param b second operand
 * @param eps precision. Defaults is 1e-4
 * @returns 0 if provided values are equal, 1 if first value is greater then second one, and -1 if first value is lower than second one
 */
export const compare = (a: number, b: number, eps: number = 1e-4) => {
  const diff = Math.abs(a - b)

  if (diff < eps) {
    return 0
  }

  return (a - b) / diff
}

export const deg2Rad = (angle: number) => {
  return (Math.PI / 180) * angle
}

export const rad2Deg = (angle: number) => {
  return (angle * 180) / Math.PI
}

export const calculateContour = (
  center_point: number[],
  thickness: number,
  length: number,
  angle: number,
) => {
  const [x, y] = center_point

  // Convert angle to radians
  const angleRad = deg2Rad(angle)

  // Calculate direction vector for the wall length and thickness
  const dx = Math.cos(angleRad)
  const dy = Math.sin(angleRad)
  const tx = -Math.sin(angleRad)
  const ty = Math.cos(angleRad)

  // Calculate the four corners of the wall
  const halfLength = length / 2
  const halfThickness = thickness / 2
  const points: Vector3[] = [
    new Vector3(
      x - halfLength * dx - halfThickness * tx,
      0,
      y - halfLength * dy - halfThickness * ty,
    ),
    new Vector3(
      x + halfLength * dx - halfThickness * tx,
      0,
      y + halfLength * dy - halfThickness * ty,
    ),
    new Vector3(
      x + halfLength * dx + halfThickness * tx,
      0,
      y + halfLength * dy + halfThickness * ty,
    ),
    new Vector3(
      x - halfLength * dx + halfThickness * tx,
      0,
      y - halfLength * dy + halfThickness * ty,
    ),
  ]

  return points
}

// #Region Ray Bounding Box Intersection
const rayLocalOrigin = new Vector3()
const rayLocalDirection = new Vector3()
const boxLocalMatrix = new Matrix()

const m = new Vector3()
const n = new Vector3()
const k = new Vector3()
const t1 = new Vector3()
const t2 = new Vector3()
export const rayBoundingBoxIntersection = (ray: Ray, box: BoundingBox) => {
  const boxWorldMatrix = box.getWorldMatrix()
  boxWorldMatrix.invertToRef(boxLocalMatrix)

  Vector3.TransformCoordinatesToRef(ray.origin, boxLocalMatrix, rayLocalOrigin)
  Vector3.TransformNormalToRef(ray.direction, boxLocalMatrix, rayLocalDirection)

  // vec3 m = 1.0/rd;
  m.setAll(1.0).divideInPlace(rayLocalDirection)
  // vec3 n = m*ro;
  n.copyFrom(m).multiplyInPlace(rayLocalOrigin)

  // vec3 k = abs(m)*boxSize;
  k.set(Math.abs(m.x), Math.abs(m.y), Math.abs(m.z)).multiplyInPlace(
    box.extendSize,
  )

  // vec3 t1 = -n - k; === -(n + k)
  n.addToRef(k, t1).scaleInPlace(-1.0)
  // vec3 t2 = -n + k; === -(n - k)
  n.subtractToRef(k, t2).scaleInPlace(-1.0)

  const tN = Math.max(t1.x, t1.y, t1.z)
  const tF = Math.min(t2.x, t2.y, t2.z)

  if (tN > tF || tF < 0.0) {
    // No intersection
    return null
  }

  const pickPoint = rayLocalDirection.scale(tN).addInPlace(rayLocalOrigin)
  const pickNormal = new Vector3()

  if (Math.abs(pickPoint.x - box.minimum.x) < 1e-3) {
    pickNormal.set(-1, 0, 0)
  } else if (Math.abs(pickPoint.x - box.maximum.x) < 1e-3) {
    pickNormal.set(1, 0, 0)
  } else if (Math.abs(pickPoint.y - box.minimum.y) < 1e-3) {
    pickNormal.set(0, -1, 0)
  } else if (Math.abs(pickPoint.y - box.maximum.y) < 1e-3) {
    pickNormal.set(0, 1, 0)
  } else if (Math.abs(pickPoint.z - box.minimum.z) < 1e-3) {
    pickNormal.set(0, 0, -1)
  } else if (Math.abs(pickPoint.z - box.maximum.z) < 1e-3) {
    pickNormal.set(0, 0, 1)
  }

  Vector3.TransformCoordinatesToRef(pickPoint, boxWorldMatrix, pickPoint)
  Vector3.TransformNormalToRef(pickNormal, boxWorldMatrix, pickNormal)

  return {
    pickPoint,
    pickNormal,
  }
}
// #
export const isParallel = (a: Vector3, b: Vector3, epsilon: number = 1e-3) => {
  if (a.length() < epsilon || b.length() < epsilon) {
    return false
  }

  const result = a.dot(b) / (a.length() * b.length())
  // Check if result is equal to +1
  return Math.abs(result - 1) < epsilon
}

export const isAntiParallel = (
  a: Vector3,
  b: Vector3,
  epsilon: number = 1e-3,
) => {
  if (a.length() < epsilon || b.length() < epsilon) {
    return false
  }

  const result = a.dot(b) / (a.length() * b.length())
  // Check if result is equal to -1
  return Math.abs(result + 1) < epsilon
}

export const isOrthoLinear = (
  a: Vector3,
  b: Vector3,
  epsilon: number = 1e-3,
) => {
  return isParallel(a, b, epsilon) || isAntiParallel(a, b, epsilon)
}

export const lineToLine2DIntersection = (
  origin1: Vector3,
  direction1: Vector3,
  origin2: Vector3,
  direction2: Vector3,
): { t: number; s: number } | null => {
  const x1 = origin1.x
  const z1 = origin1.z
  const dx1 = direction1.x
  const dz1 = direction1.z

  const x2 = origin2.x
  const z2 = origin2.z
  const dx2 = direction2.x
  const dz2 = direction2.z

  const det = dx1 * dz2 - dz1 * dx2
  if (Math.abs(det) < 1e-3) {
    return null
  }

  const dx = x2 - x1
  const dz = z2 - z1

  const t = (dx * dz2 - dz * dx2) / det
  const s = (dx * dz1 - dz * dx1) / det

  return { t, s }
}

// Credits: https://github.com/gszauer/GamePhysicsCookbook/blob/master/Code/Geometry3D.cpp#L521
export const planeBoundingBoxIntersection = (
  plane: Plane,
  box: BoundingBox,
): boolean => {
  const size = box.extendSize
  const rot = box.directions

  // Project the half extents of the AABB onto the plane normal
  const pLen =
    size.x * Math.abs(plane.normal.dot(rot[0])) +
    size.y * Math.abs(plane.normal.dot(rot[1])) +
    size.z * Math.abs(plane.normal.dot(rot[2]))

  // Find the distance from the center of the OBB to the plane
  const dist = plane.signedDistanceTo(box.centerWorld)

  // Intersection occurs if the distance falls within the projected side
  return Math.abs(dist) <= pLen
}
